import React, { useState, useEffect, useRef } from "react";
import { Search, MapPin, Layers, ArrowRight, Package, X } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { formatCurrency } from "../../utils/formatters";
import { useNavigate } from "react-router-dom";

export function GlobalProductSearch() {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { products, batches, warehouses } = useApp();
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Search Results
  const results = React.useMemo(() => {
    if (!query.trim() || query.length < 2) return [];
    const q = query.toLowerCase();

    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.barcode.includes(q) ||
          p.brand?.toLowerCase().includes(q)
      )
      .slice(0, 8)
      .map((product) => {
        // Aggregate locations from batches
        const prodBatches = batches.filter((b) => b.productId === product.id && b.status !== "Expired");
        const totalQty = prodBatches.reduce((sum, b) => sum + (Number(b.qtyRemaining) || 0), 0);

        const locationList = prodBatches.map((b) => {
          const wh = warehouses.find((w) => w.id === b.warehouseId);
          return {
            warehouseName: wh?.name?.split("(")[0]?.trim() || b.warehouseCode,
            binCode: b.binCode,
            qty: b.qtyRemaining,
            batchNo: b.batchNo
          };
        });

        return {
          ...product,
          totalQty,
          locations: locationList
        };
      });
  }, [query, products, batches, warehouses]);

  // Keyboard navigation
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, []);

  const handleSelect = (product) => {
    setIsOpen(false);
    setQuery("");
    navigate(`/inventory?search=${encodeURIComponent(product.name)}`);
  };

  return (
    <div className="relative w-full max-w-lg" ref={searchRef}>
      {/* Header Search Trigger / Input */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(0);
          }}
          placeholder="Global search: Where is it & how much? (Ctrl+K)..."
          className="w-full pl-10 pr-20 py-2 text-xs bg-slate-100 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
        />
        <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button
              onClick={() => {
                setQuery("");
                setIsOpen(false);
              }}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-xs">
            Ctrl K
          </kbd>
        </div>
      </div>

      {/* Floating Instant Result Overlay */}
      {isOpen && query.trim().length >= 2 && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden max-h-[460px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/80 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-2.5 bg-slate-50 dark:bg-slate-950/60 flex items-center justify-between text-[11px] text-slate-500">
              <span className="font-semibold uppercase tracking-wider">
                Matching Products ({results.length})
              </span>
              <span>Press enter to view details</span>
            </div>

            {results.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs">
                No matching product found for &ldquo;{query}&rdquo;
              </div>
            ) : (
              results.map((item, idx) => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className={`p-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer transition flex flex-col gap-2 ${
                    idx === selectedIndex ? "bg-emerald-50/50 dark:bg-emerald-950/20" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white font-display">
                          {item.name}
                        </h4>
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-slate-600 dark:text-slate-300 font-mono">
                          {item.barcode}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {item.category} • MRP: {formatCurrency(item.MRP)} • Wholesale: {formatCurrency(item.wholesalePrice)}
                      </p>
                    </div>

                    <div className="text-right">
                      <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded-lg">
                        Total Stock: {item.totalQty} {item.unit}s
                      </span>
                    </div>
                  </div>

                  {/* Exact Location Breakdowns */}
                  <div className="bg-slate-50/90 dark:bg-slate-950/70 p-2 rounded-xl border border-slate-200/50 dark:border-slate-800/60 space-y-1">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-emerald-500" /> Exact Bin Locations Across Warehouses:
                    </p>
                    {item.locations.length === 0 ? (
                      <p className="text-[11px] text-rose-500 italic">Out of stock in all warehouses</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-0.5">
                        {item.locations.map((loc, lIdx) => (
                          <div
                            key={lIdx}
                            className="text-[11px] flex items-center justify-between bg-white dark:bg-slate-900 px-2 py-1 rounded-md border border-slate-200/60 dark:border-slate-800"
                          >
                            <span className="text-slate-700 dark:text-slate-300 font-medium truncate max-w-[140px]">
                              {loc.warehouseName}
                            </span>
                            <span className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 rounded">
                              Bin {loc.binCode} ({loc.qty})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
